
import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, Edit3, Sparkles, ArrowRight, Smartphone, AlertCircle, Shield } from 'lucide-react';
import { dbService } from '../services/dbService';
import { processInquiryAI, optimizeContentAI } from '../services/geminiService';
import { SiteContent } from '../types';
import { CONFIG } from '../config';

interface ContactProps {
  data: SiteContent['contact'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['contact']) => void;
}

const Contact: React.FC<ContactProps> = ({ data, isAdmin, onUpdate }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'Trip Planning', message: '', honeypot: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const lastSubmitTime = useRef(0);

  const validateForm = () => {
    if (formData.honeypot) return false; // Bot detected
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid electronic mail address.');
      return false;
    }
    if (formData.name.length < 2) {
      setErrorMessage('Name is too short.');
      return false;
    }
    if (formData.message.length < 10) {
      setErrorMessage('Inquiry details are too brief for our concierge.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;

    // Basic Rate Limiting
    const now = Date.now();
    if (now - lastSubmitTime.current < CONFIG.MAX_INQUIRY_RATE_MS) {
      setErrorMessage('Please wait a few moments before sending another inquiry.');
      setStatus('error');
      return;
    }

    if (!validateForm()) {
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    
    try {
      lastSubmitTime.current = Date.now();
      const dbResult = await dbService.submitInquiry(formData);
      
      if (!dbResult.success) {
        throw new Error(dbResult.message || 'The connection to our island server was interrupted.');
      }

      const response = await processInquiryAI(formData);
      setAiResponse(response || 'Thank you for your message!');
      setStatus('success');
      
      // Clear form data on success
      setFormData({ name: '', email: '', phone: '', subject: 'Trip Planning', message: '', honeypot: '' });
      
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'The connection to our island server was interrupted.');
      setStatus('error');
    }
  };

  const titleWords = data.title.split(' ');
  const mainTitle = titleWords.length > 0 ? titleWords[0] : '';
  const italicTitle = titleWords.length > 1 ? titleWords.slice(1).join(' ') : '';

  return (
    <section id="contact" className="py-24 bg-white scroll-mt-20 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row gap-20 lg:gap-32">
          
          {/* Info Column - Integrated Editorial */}
          <div className="lg:w-5/12 py-4">
            <span className="text-sky-500 font-bold tracking-luxury uppercase text-[9px] mb-8 block">Concierge</span>
            {isAdmin ? (
              <div className="space-y-6">
                <input 
                  value={data.title}
                  onChange={e => onUpdate({...data, title: e.target.value})}
                  className="w-full bg-transparent text-4xl font-serif font-bold text-slate-900 italic focus:outline-none"
                />
                <textarea 
                  value={data.subtitle}
                  onChange={e => onUpdate({...data, subtitle: e.target.value})}
                  className="w-full bg-transparent text-slate-500 text-lg leading-relaxed font-light focus:outline-none h-24"
                />
              </div>
            ) : (
              <>
                <h2 className="text-5xl md:text-6xl font-serif mb-8 leading-none tracking-tighter text-slate-900">
                  <span className="font-bold">{mainTitle}</span> <span className="italic text-sky-500 block mt-2">{italicTitle}</span>
                </h2>
                <p className="text-slate-500 mb-16 text-xl leading-relaxed font-light">{data.subtitle}</p>
              </>
            )}

            <div className="space-y-12">
              <div className="flex items-center gap-8 group">
                <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 group-hover:bg-sky-500 transition-all duration-500">
                  <Phone className="w-5 h-5 text-sky-500 group-hover:text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-slate-400 text-[8px] font-bold uppercase tracking-luxury mb-1">Inquiry Line</p>
                  <p className="text-xl font-serif font-bold tracking-tight text-slate-800">{data.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-8 group">
                <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 group-hover:bg-sky-500 transition-all duration-500">
                  <Mail className="w-5 h-5 text-sky-500 group-hover:text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-slate-400 text-[8px] font-bold uppercase tracking-luxury mb-1">Dispatch Email</p>
                  <p className="text-xl font-serif font-bold tracking-tight text-slate-800">{data.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Column - Seamless / Panel-less */}
          <div className="lg:w-7/12">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-start justify-center text-left py-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="w-14 h-14 bg-sky-500 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-sky-500/20">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4 italic">Message Logged</h3>
                <div className="bg-slate-50 border-l-4 border-sky-400 p-8 rounded-r-2xl mb-12">
                   <p className="italic text-slate-600 text-lg leading-relaxed">"{aiResponse}"</p>
                </div>
                <div className="flex flex-col gap-6">
                  <p className="text-slate-400 text-xs font-light">
                    Note: This inquiry was sent privately to our concierge team. If you wanted to post a public message, please use the <a href="#reviews" className="text-sky-500 underline font-bold">Reviews</a> section.
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-sky-500 font-bold text-[10px] uppercase tracking-luxury hover:text-slate-900 transition-colors flex items-center gap-3 w-fit">
                    <ArrowRight className="w-4 h-4 rotate-180" /> Submit Another Inquiry
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-xl">
                <div className="flex items-center justify-between mb-12">
                  <h3 className="text-2xl font-serif font-bold text-slate-900 italic">{data.formTitle}</h3>
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                    <Shield className="w-3 h-3" /> Private Channel
                  </div>
                </div>
                
                {status === 'error' && (
                  <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-xs font-bold animate-in fade-in slide-in-from-top-2">
                    <AlertCircle className="w-4 h-4" />
                    {errorMessage}
                  </div>
                )}

                <form className="space-y-16" onSubmit={handleSubmit}>
                  {/* Honeypot Field */}
                  <div className="hidden">
                    <input 
                      type="text" 
                      autoComplete="off"
                      value={formData.honeypot} 
                      onChange={e => setFormData({...formData, honeypot: e.target.value})} 
                      placeholder="Do not fill this" 
                    />
                  </div>

                  <div className="space-y-16">
                    <div className="relative group">
                      <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400 mb-2">Full Name</p>
                      <input 
                        required 
                        type="text" 
                        value={formData.name} 
                        onChange={e => setFormData({...formData, name: e.target.value})} 
                        className="w-full bg-transparent border-b border-slate-100 py-4 text-lg focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200 text-slate-800" 
                        placeholder="e.g. Elena Soriano" 
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                      <div className="relative group">
                        <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400 mb-2">Electronic Mail</p>
                        <input 
                          required 
                          type="email" 
                          value={formData.email} 
                          onChange={e => setFormData({...formData, email: e.target.value})} 
                          className="w-full bg-transparent border-b border-slate-100 py-4 text-lg focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200 text-slate-800" 
                          placeholder="concierge@travel.com" 
                        />
                      </div>
                      
                      <div className="relative group">
                        <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400 mb-2">Mobile Number</p>
                        <input 
                          type="tel" 
                          value={formData.phone} 
                          onChange={e => setFormData({...formData, phone: e.target.value})} 
                          className="w-full bg-transparent border-b border-slate-100 py-4 text-lg focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200 text-slate-800" 
                          placeholder={data.phonePlaceholder} 
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative group">
                    <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400 mb-2">Expedition Inquiry</p>
                    <textarea 
                      required 
                      rows={4} 
                      value={formData.message} 
                      onChange={e => setFormData({...formData, message: e.target.value})} 
                      className="w-full bg-transparent border-b border-slate-100 py-4 text-lg focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200 text-slate-800 resize-none font-serif italic" 
                      placeholder="Share your voyage requirements..."
                    ></textarea>
                  </div>

                  <div className="flex flex-col gap-6">
                    <button 
                      disabled={status === 'loading'} 
                      className="group flex items-center gap-4 md:gap-6 bg-slate-950 hover:bg-sky-500 text-white px-8 md:px-14 py-4 md:py-7 rounded-xl md:rounded-2xl font-bold transition-all text-[10px] uppercase tracking-luxury shadow-xl active:scale-95 disabled:opacity-50 w-fit"
                    >
                      {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{data.buttonLabel} <ArrowRight className="w-5 h-5 group-hover:translate-x-3 transition-transform" /></>}
                    </button>
                    <p className="text-slate-400 text-[9px] font-medium uppercase tracking-[0.2em] italic">
                      Inquiries sent via this form are private and will not appear in the public reviews list.
                    </p>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
